import React, { FC, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownWideShort, faArrowUpWideShort, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useTodoContext } from "../../todos/useTodoContext";
import { TodoItem } from "./TodoItem";
import { TodoEntityType } from "../../api/dto/Todo";

interface TodoListProps {
  relationId?: string
  projectId?: string
  quoteId?: string
  orderId?: string
  hideDoneItems?: boolean
  createOnEntity?: { type: TodoEntityType, id: string }
}

export const TodoList: FC<TodoListProps> = (props) => {
  const { todos, error, loading, createTodo } = useTodoContext()
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [newTodoText, setNewTodoText] = useState('')

  const excludeType: TodoEntityType | undefined = props.relationId !== undefined ? 'relation'
    : props.projectId !== undefined ? 'project'
    : props.quoteId !== undefined ? 'quote'
    : props.orderId !== undefined ? 'order'
    : undefined

  const filteredTodos = useMemo(() => {
    const filtered = todos.filter(todo => {
      if (props.relationId !== undefined) return todo.relationId === props.relationId
      if (props.projectId !== undefined) return todo.projectId === props.projectId
      if (props.quoteId !== undefined) return todo.quoteId === props.quoteId
      if (props.orderId !== undefined) return todo.orderId === props.orderId
      return true
    }).filter(todo => props.hideDoneItems === true ? !todo.isDone : true)
    return [...filtered].sort((a, b) => sortDirection === 'asc'
      ? a.createdAt.getTime() - b.createdAt.getTime()
      : b.createdAt.getTime() - a.createdAt.getTime())
  }, [todos, sortDirection, props.relationId, props.projectId, props.quoteId, props.orderId, props.hideDoneItems])

  const onCreate = async () => {
    const text = newTodoText.trim()
    if (text.length === 0) return
    try {
      await createTodo(text, props.createOnEntity?.type, props.createOnEntity?.id)
      // Only clear the input after a successful create — on failure the typed text stays so
      // the user can retry instead of silently losing it.
      setNewTodoText('')
    } catch {
      // Swallowed: the input is deliberately left as-is, see comment above.
    }
  }

  return <div className={"flex flex-col"}>
    <div className={"flex items-center justify-between mb-2"}>
      <div className={"text-xs text-slate-500"}>{filteredTodos.length} to-do{filteredTodos.length === 1 ? '' : "'s"}</div>
      <button
        className={"text-xs text-slate-500 hover:text-brand-700"}
        onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
      >
        <FontAwesomeIcon icon={sortDirection === 'asc' ? faArrowUpWideShort : faArrowDownWideShort} className={"mr-1"} />
        Aanmaakdatum
      </button>
    </div>
    <div className={"flex flex-col"}>
      {error !== null ? (
        <div className={"text-sm text-red-600 italic py-2"}>Kon to-do's niet laden</div>
      ) : <>
        {filteredTodos.map(todo => <TodoItem key={todo.id} todo={todo} excludeType={excludeType} />)}
        {!loading && filteredTodos.length === 0 && <div className={"text-sm text-slate-400 italic py-2"}>Geen to-do's</div>}
      </>}
    </div>
    <div className={"flex items-center space-x-2 mt-2 pt-2 border-t border-slate-100"}>
      <input
        className={"flex-1 text-sm border-b border-slate-300 outline-none py-1"}
        placeholder={"Nieuwe to-do..."}
        value={newTodoText}
        onChange={e => setNewTodoText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') void onCreate()
        }}
      />
      <button onClick={onCreate} className={"text-brand-700 hover:text-brand-900"}>
        <FontAwesomeIcon icon={faPlus} />
      </button>
    </div>
  </div>
}
